import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RiAddLine, RiCheckboxCircleLine, RiCloseCircleLine, RiDeleteBinLine, RiFileCopyLine, RiPulseLine } from '@remixicon/react';
import {
  connectorConfigInputSchema,
  FILE_IMPORT_DELIMITERS,
  FILE_IMPORT_FORMATS,
  MODBUS_DATA_TYPES,
  MODBUS_REGISTER_KINDS,
  MQTT_PAYLOAD_FORMATS,
  OPCUA_SECURITY_MODES,
  REST_AUTH_KINDS,
  WEBHOOK_PAYLOAD_FORMATS,
  type ConnectorConfigInput,
  type ConnectorDto,
  type ConnectorScope,
  type ConnectorTestResult,
  type ConnectorType,
  type CreateConnectorInput,
  type RestAuthKind,
  type SecretHint,
  type UpdateConnectorInput,
} from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import {
  Badge,
  Button,
  Field,
  FieldCaption,
  IconButton,
  Input,
  InputAction,
  PasswordInput,
  SegmentedControl,
  Select,
  Switch,
  Textarea,
  toast,
  type SelectOption,
} from '@/shared/ui';
import { formatReadingTime } from '@/features/devices/format';
import type { ConnectorsContext } from '../backend';
import { useTestConnector } from '../api';
import { webhookUrl } from '../util';

/* ───────────── draft ───────────── */

type ModbusKind = (typeof MODBUS_REGISTER_KINDS)[number];
type ModbusDataType = (typeof MODBUS_DATA_TYPES)[number];

export interface RegisterDraft {
  key: string;
  address: string;
  name: string;
  kind: ModbusKind;
  dataType: ModbusDataType;
  scale: string;
  unit: string;
}

/** Form state of a connector (text inputs stay strings until `buildInput`). */
export interface ConnectorDraft {
  id: string;
  type: ConnectorType;
  name: string;
  description: string;
  scope: ConnectorScope;
  enabled: boolean;
  url: string;
  pollIntervalSec: string;
  /** new value of the type's secret ('' = keep the stored one / none) */
  secret: string;
  /** what is stored (edit mode) — the secret field shows it as a placeholder */
  storedSecret: SecretHint | null;
  storedAuthKind: RestAuthKind | null;
  siteCode: string;
  authKind: RestAuthKind;
  headerName: string;
  sensorsPath: string;
  valuesPath: string;
  idPath: string;
  valuePath: string;
  timePath: string;
  topic: string;
  username: string;
  payloadFormat: string;
  securityMode: (typeof OPCUA_SECURITY_MODES)[number];
  nodeIds: string;
  host: string;
  port: string;
  unitId: string;
  registers: RegisterDraft[];
  format: (typeof FILE_IMPORT_FORMATS)[number];
  delimiter: (typeof FILE_IMPORT_DELIMITERS)[number];
  decimalComma: boolean;
  headerRow: string;
  timeColumn: string;
  timeFormat: string;
  columns: string;
}

const newKey = () => Math.random().toString(36).slice(2, 10);
const randomId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(16)}-${newKey()}`);

/** 32 random base62 characters with a `whsec_` prefix. */
export function generateSecret() {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return `whsec_${Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')}`;
}

const emptyRegister = (): RegisterDraft => ({ key: newKey(), address: '', name: '', kind: 'holding', dataType: 'uint16', scale: '1', unit: '' });

const BASE: Omit<ConnectorDraft, 'id' | 'type' | 'scope' | 'siteCode'> = {
  name: '',
  description: '',
  enabled: true,
  url: '',
  pollIntervalSec: '60',
  secret: '',
  storedSecret: null,
  storedAuthKind: null,
  authKind: 'bearer',
  headerName: 'X-API-Key',
  sensorsPath: '/sensors',
  valuesPath: '/readings/latest',
  idPath: '$.id',
  valuePath: '$.value',
  timePath: '$.timestamp',
  topic: 'gidro/+/telemetry',
  username: '',
  payloadFormat: 'json',
  securityMode: 'sign_encrypt',
  nodeIds: '',
  host: '',
  port: '502',
  unitId: '1',
  registers: [],
  format: 'csv',
  delimiter: ',',
  decimalComma: false,
  headerRow: '1',
  timeColumn: 'Date Time',
  timeFormat: 'YYYY-MM-DD HH:mm:ss',
  columns: '',
};

/** Draft of a new connector of `type`. */
export function newDraft(type: ConnectorType, siteCode: string | null): ConnectorDraft {
  const d: ConnectorDraft = { ...BASE, id: randomId(), type, scope: 'tenant', siteCode: siteCode ?? '' };
  switch (type) {
    case 'atnt_unified':
      return { ...d, url: 'https://monitoring.uzgidro.uz/api', pollIntervalSec: '300' };
    case 'mqtt':
      return { ...d, url: 'mqtts://' };
    case 'opcua':
      return { ...d, url: 'opc.tcp://', pollIntervalSec: '30' };
    case 'modbus_tcp':
      return { ...d, pollIntervalSec: '10', registers: [emptyRegister()] };
    case 'webhook':
      return { ...d, secret: generateSecret() };
    default:
      return d;
  }
}

/** Draft of an existing connector (secrets are never known — only their hints). */
export function draftOf(c: ConnectorDto): ConnectorDraft {
  const d: ConnectorDraft = { ...BASE, id: c.id, type: c.type, scope: c.scope, name: c.name, description: c.description ?? '', enabled: c.enabled, siteCode: '' };
  const cfg = c.config;
  switch (cfg.type) {
    case 'atnt_unified':
      return { ...d, url: cfg.baseUrl, pollIntervalSec: String(cfg.pollIntervalSec), storedSecret: cfg.apiKey, siteCode: cfg.siteCode ?? '' };
    case 'rest':
      return {
        ...d,
        url: cfg.baseUrl,
        pollIntervalSec: String(cfg.pollIntervalSec),
        authKind: cfg.auth.kind,
        storedAuthKind: cfg.auth.kind,
        headerName: cfg.auth.kind === 'header' ? cfg.auth.headerName : BASE.headerName,
        storedSecret: cfg.auth.kind === 'header' ? cfg.auth.value : cfg.auth.kind === 'bearer' ? cfg.auth.token : null,
        sensorsPath: cfg.sensorsPath,
        valuesPath: cfg.valuesPath,
        idPath: cfg.idPath,
        valuePath: cfg.valuePath,
        timePath: cfg.timePath,
      };
    case 'mqtt':
      return { ...d, url: cfg.brokerUrl, topic: cfg.topic, username: cfg.username ?? '', storedSecret: cfg.password, payloadFormat: cfg.payloadFormat };
    case 'opcua':
      return {
        ...d,
        url: cfg.endpointUrl,
        securityMode: cfg.securityMode,
        username: cfg.username ?? '',
        storedSecret: cfg.password,
        nodeIds: cfg.nodeIds.join('\n'),
        pollIntervalSec: String(cfg.pollIntervalSec),
      };
    case 'modbus_tcp':
      return {
        ...d,
        host: cfg.host,
        port: String(cfg.port),
        unitId: String(cfg.unitId),
        pollIntervalSec: String(cfg.pollIntervalSec),
        registers: cfg.registers.map((r) => ({ key: newKey(), address: String(r.address), name: r.name, kind: r.kind, dataType: r.dataType, scale: String(r.scale), unit: r.unit ?? '' })),
      };
    case 'webhook':
      return { ...d, url: cfg.endpointUrl, payloadFormat: cfg.payloadFormat, storedSecret: cfg.secret };
    case 'file_import':
      return {
        ...d,
        format: cfg.format,
        delimiter: cfg.delimiter,
        decimalComma: cfg.decimalComma,
        headerRow: String(cfg.headerRow),
        timeColumn: cfg.timeColumn,
        timeFormat: cfg.timeFormat,
        columns: cfg.columns.map((col) => [col.column, col.unit].filter(Boolean).join(' | ')).join('\n'),
      };
  }
}

const num = (s: string) => (s.trim() === '' ? Number.NaN : Number(s.trim().replace(',', '.')));
const lines = (s: string) =>
  s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

function configOf(d: ConnectorDraft): ConnectorConfigInput {
  const secret = d.secret || undefined;
  const poll = num(d.pollIntervalSec);
  switch (d.type) {
    case 'atnt_unified':
      return { type: 'atnt_unified', baseUrl: d.url.trim(), apiKey: secret, pollIntervalSec: poll };
    case 'rest':
      return {
        type: 'rest',
        baseUrl: d.url.trim(),
        auth: d.authKind === 'none' ? { kind: 'none' } : d.authKind === 'header' ? { kind: 'header', headerName: d.headerName.trim(), value: secret } : { kind: 'bearer', token: secret },
        sensorsPath: d.sensorsPath,
        valuesPath: d.valuesPath,
        idPath: d.idPath,
        valuePath: d.valuePath,
        timePath: d.timePath,
        pollIntervalSec: poll,
      };
    case 'mqtt':
      return {
        type: 'mqtt',
        brokerUrl: d.url.trim(),
        topic: d.topic,
        username: d.username.trim() || null,
        password: secret,
        payloadFormat: d.payloadFormat as (typeof MQTT_PAYLOAD_FORMATS)[number],
      };
    case 'opcua':
      return {
        type: 'opcua',
        endpointUrl: d.url.trim(),
        securityMode: d.securityMode,
        username: d.username.trim() || null,
        password: secret,
        nodeIds: lines(d.nodeIds),
        pollIntervalSec: poll,
      };
    case 'modbus_tcp':
      return {
        type: 'modbus_tcp',
        host: d.host.trim(),
        port: num(d.port),
        unitId: num(d.unitId),
        pollIntervalSec: poll,
        registers: d.registers.map((r) => ({
          address: num(r.address),
          name: r.name,
          kind: r.kind,
          dataType: r.dataType,
          scale: r.scale.trim() === '' ? 1 : num(r.scale),
          unit: r.unit.trim() || null,
        })),
      };
    case 'webhook':
      return { type: 'webhook', payloadFormat: d.payloadFormat as (typeof WEBHOOK_PAYLOAD_FORMATS)[number], secret };
    case 'file_import':
      return {
        type: 'file_import',
        format: d.format,
        delimiter: d.delimiter,
        decimalComma: d.decimalComma,
        headerRow: num(d.headerRow),
        timeColumn: d.timeColumn,
        timeFormat: d.timeFormat,
        columns: lines(d.columns).map((l) => {
          const [column, unit] = l.split('|').map((p) => p.trim());
          return { column: column ?? '', unit: unit || null };
        }),
      };
  }
}

/** Zod path → draft field (errors are shown under the matching control). */
function fieldOf(path: PropertyKey[]): string {
  const [head, second, third] = path.map(String);
  if (head === 'baseUrl' || head === 'brokerUrl' || head === 'endpointUrl') return 'url';
  if (head === 'apiKey' || head === 'password' || head === 'secret') return 'secret';
  if (head === 'auth') return second === 'headerName' ? 'headerName' : 'secret';
  if (head === 'registers' && second != null && third != null) return `registers.${second}.${third}`;
  if (head === 'registers') return 'registers';
  return head ?? 'form';
}

const ERROR_KEY: Record<string, string> = {
  url: 'errors.url',
  pollIntervalSec: 'errors.interval',
  headerName: 'errors.header',
  sensorsPath: 'errors.path',
  valuesPath: 'errors.path',
  idPath: 'errors.jsonPath',
  valuePath: 'errors.jsonPath',
  timePath: 'errors.jsonPath',
  host: 'errors.host',
  port: 'errors.port',
  unitId: 'errors.unitId',
  nodeIds: 'errors.nodeIds',
  columns: 'errors.columns',
  registers: 'errors.registers',
  headerRow: 'errors.number',
  secret: 'errors.secret',
};

const needsSecret = (d: ConnectorDraft) => d.type === 'atnt_unified' || (d.type === 'rest' && d.authKind !== 'none');
const hasStoredSecret = (d: ConnectorDraft) => Boolean(d.storedSecret?.set) && (d.type !== 'rest' || d.storedAuthKind === d.authKind);

/**
 * Validate the draft with the shared zod schemas. Returns the API input (create or update) or the field errors.
 * `forTest` skips the name (a connection can be tested before it has a name).
 */
export function buildInput(
  d: ConnectorDraft,
  mode: 'create' | 'edit',
  t: (key: string) => string,
  forTest = false,
): { input: CreateConnectorInput | UpdateConnectorInput; config: ConnectorConfigInput; errors?: undefined } | { errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const config = configOf(d);
  const parsed = connectorConfigInputSchema.safeParse(config);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = fieldOf(issue.path);
      if (errors[field]) continue;
      const leaf = field.split('.').pop() ?? field;
      const empty = issue.code === 'too_small' && 'origin' in issue && (issue.origin === 'string' || issue.origin === 'array');
      errors[field] = t(empty && leaf !== 'nodeIds' && leaf !== 'columns' && leaf !== 'registers' ? 'errors.required' : (ERROR_KEY[leaf] ?? (leaf === 'address' ? 'errors.address' : 'errors.invalid')));
    }
  }
  if (needsSecret(d) && !d.secret && !hasStoredSecret(d)) errors.secret = t('errors.secret');
  if (!forTest && !d.name.trim()) errors.name = t('errors.required');
  if (Object.keys(errors).length) return { errors };
  const base = { name: d.name.trim(), description: d.description.trim() || null, enabled: d.enabled, config };
  return { input: mode === 'create' ? { ...base, id: d.id, scope: d.scope } : base, config };
}

/* ───────────── fields ───────────── */

function copy(text: string, t: (k: string) => string) {
  void navigator.clipboard
    .writeText(text)
    .then(() => toast.success(t('form.copied')))
    .catch(() => toast.error(t('form.copyFailed')));
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div aria-hidden className="h-px shrink-0 bg-line" />
      <FieldCaption as="h4">{title}</FieldCaption>
      {children}
    </>
  );
}

const opts = <T extends string>(values: readonly T[], label: (v: T) => string): SelectOption<T>[] => values.map((value) => ({ value, label: label(value) }));

/**
 * Fields of the connector form: name, description, scope, the type-specific connection settings and the enabled
 * switch. Used by the create / edit side panel and the "Sozlamalar" tab of the detail page.
 */
export function ConnectorFields({
  draft: d,
  onChange,
  errors,
  mode,
  canChooseOrg,
  readOnly,
  compact,
}: {
  draft: ConnectorDraft;
  onChange: (patch: Partial<ConnectorDraft>) => void;
  errors: Record<string, string>;
  mode: 'create' | 'edit';
  canChooseOrg: boolean;
  readOnly: boolean;
  /** single column (side panel); wide layouts put short fields side by side */
  compact?: boolean;
}) {
  const { t } = useTranslation('connectors');
  const grid = compact ? 'grid grid-cols-2 gap-10' : 'grid grid-cols-2 gap-14';
  const secretPlaceholder = hasStoredSecret(d) ? t('form.secretStored', { hint: d.storedSecret?.hint ?? '••••' }) : t('form.secretPlaceholder');
  const pwLabels = { show: t('form.show'), hide: t('form.hide'), copy: t('form.copy'), copied: t('form.copied'), regenerate: t('form.regenerate') };

  const poll = (
    <Field label={t('form.pollInterval')} error={errors.pollIntervalSec} hint={errors.pollIntervalSec ? undefined : t('form.pollHint')}>
      <Input inputMode="numeric" value={d.pollIntervalSec} onChange={(e) => onChange({ pollIntervalSec: e.currentTarget.value })} rightSlot={<span className="text-xs text-fg-tertiary">{t('form.sec')}</span>} />
    </Field>
  );
  const secretField = (label: string, hint?: string) => (
    <Field label={label} error={errors.secret} hint={errors.secret ? undefined : hint}>
      <PasswordInput
        value={d.secret}
        placeholder={secretPlaceholder}
        labels={pwLabels}
        autoComplete="off"
        maxLength={1000}
        onChange={(e) => onChange({ secret: e.currentTarget.value })}
      />
    </Field>
  );

  let specific: ReactNode = null;
  switch (d.type) {
    case 'atnt_unified':
      specific = (
        <>
          <Field label={t('form.baseUrl')} error={errors.url} hint={errors.url ? undefined : t('form.baseUrlHint')}>
            <Input value={d.url} maxLength={500} spellCheck={false} placeholder="https://monitoring.uzgidro.uz/api" onChange={(e) => onChange({ url: e.currentTarget.value })} />
          </Field>
          {secretField(t('form.apiKey'), t('form.apiKeyHint'))}
          <div className={grid}>
            <Field label={t('form.siteCode')} hint={t('form.siteCodeHint')}>
              <Input value={d.siteCode} readOnly placeholder={t('form.siteCodeEmpty')} />
            </Field>
            {poll}
          </div>
        </>
      );
      break;
    case 'rest':
      specific = (
        <>
          <Field label={t('form.baseUrl')} error={errors.url}>
            <Input value={d.url} maxLength={500} spellCheck={false} placeholder="https://api.example.uz/v1" onChange={(e) => onChange({ url: e.currentTarget.value })} />
          </Field>
          <Field label={t('form.auth')}>
            <SegmentedControl<RestAuthKind>
              fullWidth
              size="md"
              aria-label={t('form.auth')}
              value={d.authKind}
              disabled={readOnly}
              onValueChange={(authKind) => onChange({ authKind, secret: '' })}
              items={REST_AUTH_KINDS.map((k) => ({ value: k, label: t(`form.authKinds.${k}`) }))}
            />
          </Field>
          {d.authKind === 'header' && (
            <div className={grid}>
              <Field label={t('form.headerName')} error={errors.headerName}>
                <Input value={d.headerName} maxLength={100} spellCheck={false} onChange={(e) => onChange({ headerName: e.currentTarget.value })} />
              </Field>
              {secretField(t('form.headerValue'))}
            </div>
          )}
          {d.authKind === 'bearer' && secretField(t('form.token'))}
          <div className={grid}>
            <Field label={t('form.sensorsPath')} error={errors.sensorsPath}>
              <Input value={d.sensorsPath} spellCheck={false} onChange={(e) => onChange({ sensorsPath: e.currentTarget.value })} />
            </Field>
            <Field label={t('form.valuesPath')} error={errors.valuesPath}>
              <Input value={d.valuesPath} spellCheck={false} onChange={(e) => onChange({ valuesPath: e.currentTarget.value })} />
            </Field>
          </div>
          <div className={compact ? 'grid grid-cols-3 gap-10' : 'grid grid-cols-3 gap-14'}>
            <Field label={t('form.idPath')} error={errors.idPath}>
              <Input value={d.idPath} spellCheck={false} onChange={(e) => onChange({ idPath: e.currentTarget.value })} />
            </Field>
            <Field label={t('form.valuePath')} error={errors.valuePath}>
              <Input value={d.valuePath} spellCheck={false} onChange={(e) => onChange({ valuePath: e.currentTarget.value })} />
            </Field>
            <Field label={t('form.timePath')} error={errors.timePath}>
              <Input value={d.timePath} spellCheck={false} onChange={(e) => onChange({ timePath: e.currentTarget.value })} />
            </Field>
          </div>
          {poll}
        </>
      );
      break;
    case 'mqtt':
      specific = (
        <>
          <Field label={t('form.brokerUrl')} error={errors.url} hint={errors.url ? undefined : t('form.brokerHint')}>
            <Input value={d.url} maxLength={500} spellCheck={false} placeholder="mqtts://broker.example.uz:8883" onChange={(e) => onChange({ url: e.currentTarget.value })} />
          </Field>
          <Field label={t('form.topic')} error={errors.topic} hint={errors.topic ? undefined : t('form.topicHint')}>
            <Input value={d.topic} maxLength={300} spellCheck={false} onChange={(e) => onChange({ topic: e.currentTarget.value })} />
          </Field>
          <div className={grid}>
            <Field label={t('form.username')}>
              <Input value={d.username} maxLength={200} autoComplete="off" onChange={(e) => onChange({ username: e.currentTarget.value })} />
            </Field>
            {secretField(t('form.password'))}
          </div>
          <Field label={t('form.payloadFormat')}>
            <SegmentedControl
              fullWidth
              aria-label={t('form.payloadFormat')}
              value={d.payloadFormat}
              disabled={readOnly}
              onValueChange={(payloadFormat) => onChange({ payloadFormat })}
              items={MQTT_PAYLOAD_FORMATS.map((f) => ({ value: f, label: f.toUpperCase() }))}
            />
          </Field>
        </>
      );
      break;
    case 'opcua':
      specific = (
        <>
          <Field label={t('form.endpointUrl')} error={errors.url}>
            <Input value={d.url} maxLength={500} spellCheck={false} placeholder="opc.tcp://10.0.4.20:4840" onChange={(e) => onChange({ url: e.currentTarget.value })} />
          </Field>
          <Field label={t('form.securityMode')}>
            <Select
              aria-label={t('form.securityMode')}
              value={d.securityMode}
              disabled={readOnly}
              onValueChange={(v) => onChange({ securityMode: v ?? 'none' })}
              options={opts(OPCUA_SECURITY_MODES, (m) => t(`form.security.${m}`))}
            />
          </Field>
          <div className={grid}>
            <Field label={t('form.username')}>
              <Input value={d.username} maxLength={200} autoComplete="off" onChange={(e) => onChange({ username: e.currentTarget.value })} />
            </Field>
            {secretField(t('form.password'))}
          </div>
          <Field label={t('form.nodeIds')} error={errors.nodeIds} hint={errors.nodeIds ? undefined : t('form.nodeIdsHint')}>
            <Textarea rows={4} value={d.nodeIds} spellCheck={false} placeholder={'ns=2;s=Dam.Level\nns=2;s=Dam.Pz01'} onChange={(e) => onChange({ nodeIds: e.currentTarget.value })} />
          </Field>
          {poll}
        </>
      );
      break;
    case 'modbus_tcp':
      specific = (
        <>
          <div className={compact ? 'grid grid-cols-[1fr_5rem_4.5rem] gap-10' : 'grid grid-cols-[1fr_8rem_8rem] gap-14'}>
            <Field label={t('form.host')} error={errors.host}>
              <Input value={d.host} maxLength={253} spellCheck={false} placeholder="10.0.4.12" onChange={(e) => onChange({ host: e.currentTarget.value })} />
            </Field>
            <Field label={t('form.port')} error={errors.port}>
              <Input inputMode="numeric" value={d.port} onChange={(e) => onChange({ port: e.currentTarget.value })} />
            </Field>
            <Field label={t('form.unitId')} error={errors.unitId}>
              <Input inputMode="numeric" value={d.unitId} onChange={(e) => onChange({ unitId: e.currentTarget.value })} />
            </Field>
          </div>
          {poll}
          <RegistersEditor draft={d} onChange={onChange} errors={errors} readOnly={readOnly} />
        </>
      );
      break;
    case 'webhook': {
      const url = d.url || webhookUrl(d.id);
      specific = (
        <>
          <Field label={t('form.webhookUrl')} hint={t('form.webhookUrlHint')}>
            <Input
              value={url}
              readOnly
              spellCheck={false}
              inputClassName="text-fg-secondary"
              actions={<InputAction icon={RiFileCopyLine} aria-label={t('form.copy')} onClick={() => copy(url, t)} />}
            />
          </Field>
          <Field label={t('form.webhookSecret')} error={errors.secret} hint={d.secret ? t('form.webhookSecretOnce') : t('form.webhookSecretHint')}>
            <PasswordInput
              value={d.secret}
              readOnly
              placeholder={secretPlaceholder}
              labels={pwLabels}
              copyable={Boolean(d.secret)}
              onCopied={() => toast.success(t('form.copied'))}
              onRegenerate={readOnly ? undefined : () => onChange({ secret: generateSecret() })}
            />
          </Field>
          <Field label={t('form.payloadFormat')}>
            <SegmentedControl
              fullWidth
              aria-label={t('form.payloadFormat')}
              value={d.payloadFormat}
              disabled={readOnly}
              onValueChange={(payloadFormat) => onChange({ payloadFormat })}
              items={WEBHOOK_PAYLOAD_FORMATS.map((f) => ({ value: f, label: f.toUpperCase() }))}
            />
          </Field>
        </>
      );
      break;
    }
    case 'file_import':
      specific = (
        <>
          <div className={grid}>
            <Field label={t('form.fileFormat')}>
              <SegmentedControl
                fullWidth
                aria-label={t('form.fileFormat')}
                value={d.format}
                disabled={readOnly}
                onValueChange={(format) => onChange({ format })}
                items={FILE_IMPORT_FORMATS.map((f) => ({ value: f, label: f.toUpperCase() }))}
              />
            </Field>
            <Field label={t('form.delimiter')}>
              <Select
                aria-label={t('form.delimiter')}
                value={d.delimiter}
                disabled={readOnly || d.format !== 'csv'}
                onValueChange={(v) => onChange({ delimiter: v ?? ',' })}
                options={opts(FILE_IMPORT_DELIMITERS, (v) => t(`form.delimiters.${v === '\t' ? 'tab' : v === ';' ? 'semicolon' : 'comma'}`))}
              />
            </Field>
          </div>
          <div className={grid}>
            <Field label={t('form.timeColumn')} error={errors.timeColumn}>
              <Input value={d.timeColumn} maxLength={120} onChange={(e) => onChange({ timeColumn: e.currentTarget.value })} />
            </Field>
            <Field label={t('form.timeFormat')} error={errors.timeFormat}>
              <Input value={d.timeFormat} maxLength={64} spellCheck={false} onChange={(e) => onChange({ timeFormat: e.currentTarget.value })} />
            </Field>
          </div>
          <div className={grid}>
            <Field label={t('form.headerRow')} error={errors.headerRow}>
              <Input inputMode="numeric" value={d.headerRow} onChange={(e) => onChange({ headerRow: e.currentTarget.value })} />
            </Field>
            <Field label={t('form.decimal')}>
              <SegmentedControl
                fullWidth
                aria-label={t('form.decimal')}
                value={d.decimalComma ? 'comma' : 'dot'}
                disabled={readOnly}
                onValueChange={(v) => onChange({ decimalComma: v === 'comma' })}
                items={[
                  { value: 'dot', label: '12.5' },
                  { value: 'comma', label: '12,5' },
                ]}
              />
            </Field>
          </div>
          <Field label={t('form.columns')} error={errors.columns} hint={errors.columns ? undefined : t('form.columnsHint')}>
            <Textarea rows={5} value={d.columns} spellCheck={false} placeholder={'Pz1_kPa | kPa\nPz2_kPa | kPa\nPTemp_C | °C'} onChange={(e) => onChange({ columns: e.currentTarget.value })} />
          </Field>
        </>
      );
      break;
  }

  return (
    <fieldset disabled={readOnly} className="flex min-w-0 flex-col gap-14">
      <Field label={t('form.name')} error={errors.name}>
        <Input
          // oxlint-disable-next-line jsx-a11y/no-autofocus -- the panel opens from an explicit action; its first field takes focus
          autoFocus={mode === 'create'}
          value={d.name}
          maxLength={160}
          placeholder={t('form.namePlaceholder')}
          onChange={(e) => onChange({ name: e.currentTarget.value })}
        />
      </Field>
      <Field label={t('form.description')}>
        <Textarea rows={2} value={d.description} maxLength={1000} placeholder={t('form.descriptionPlaceholder')} onChange={(e) => onChange({ description: e.currentTarget.value })} />
      </Field>
      <Field label={t('form.scope')} hint={mode === 'edit' ? t('form.scopeFixed') : canChooseOrg ? t('form.scopeHint') : t('form.scopeOrgOnly')}>
        <SegmentedControl<ConnectorScope>
          fullWidth
          aria-label={t('form.scope')}
          value={d.scope}
          disabled={readOnly || mode === 'edit'}
          onValueChange={(scope) => onChange({ scope })}
          items={[
            { value: 'tenant', label: t('scopes.tenant') },
            { value: 'organization', label: t('scopes.organization'), disabled: !canChooseOrg },
          ]}
        />
      </Field>
      <Section title={t('form.connection')}>{specific}</Section>
      <div aria-hidden className="h-px shrink-0 bg-line" />
      <Switch checked={d.enabled} disabled={readOnly} onCheckedChange={(enabled) => onChange({ enabled })} label={t('form.enabled')} />
    </fieldset>
  );
}

/** Modbus registers: one compact card per register (name, address, kind, data type, scale, unit). */
function RegistersEditor({
  draft: d,
  onChange,
  errors,
  readOnly,
}: {
  draft: ConnectorDraft;
  onChange: (patch: Partial<ConnectorDraft>) => void;
  errors: Record<string, string>;
  readOnly: boolean;
}) {
  const { t } = useTranslation('connectors');
  const update = (key: string, patch: Partial<RegisterDraft>) => onChange({ registers: d.registers.map((r) => (r.key === key ? { ...r, ...patch } : r)) });
  return (
    <div className="flex flex-col gap-8">
      <div className="flex min-h-24 items-center gap-8">
        <FieldCaption as="span" className="min-w-0 flex-1">
          {t('form.registers')}
        </FieldCaption>
        <Badge color="neutral">{d.registers.length}</Badge>
        {!readOnly && (
          <Button size="sm" variant="secondary" leftIcon={RiAddLine} onClick={() => onChange({ registers: [...d.registers, emptyRegister()] })}>
            {t('form.addRegister')}
          </Button>
        )}
      </div>
      {errors.registers && <p className="text-11 text-tone-red-lighter">{errors.registers}</p>}
      <ul className="flex flex-col gap-6">
        {d.registers.map((r, i) => (
          <li key={r.key} className="flex flex-col gap-6 rounded-8 border border-line-subtle bg-field p-8">
            <div className="grid grid-cols-[1fr_5.5rem_auto] items-start gap-6">
              <Input
                aria-label={`${t('form.registerName')} ${i + 1}`}
                placeholder={t('form.registerName')}
                value={r.name}
                maxLength={120}
                invalid={Boolean(errors[`registers.${i}.name`])}
                onChange={(e) => update(r.key, { name: e.currentTarget.value })}
              />
              <Input
                aria-label={`${t('form.address')} ${i + 1}`}
                placeholder={t('form.address')}
                inputMode="numeric"
                value={r.address}
                invalid={Boolean(errors[`registers.${i}.address`])}
                onChange={(e) => update(r.key, { address: e.currentTarget.value })}
              />
              <IconButton
                icon={RiDeleteBinLine}
                size="md"
                variant="danger"
                aria-label={`${t('form.removeRegister')} ${i + 1}`}
                disabled={readOnly || d.registers.length <= 1}
                onClick={() => onChange({ registers: d.registers.filter((x) => x.key !== r.key) })}
              />
            </div>
            <div className="grid grid-cols-[1fr_1fr_3.5rem_3.5rem] gap-6">
              <Select
                aria-label={`${t('form.registerKind')} ${i + 1}`}
                size="md"
                value={r.kind}
                disabled={readOnly}
                onValueChange={(v) => update(r.key, { kind: v ?? 'holding' })}
                options={opts(MODBUS_REGISTER_KINDS, (k) => t(`form.registerKinds.${k}`))}
              />
              <Select
                aria-label={`${t('form.dataType')} ${i + 1}`}
                size="md"
                value={r.dataType}
                disabled={readOnly}
                onValueChange={(v) => update(r.key, { dataType: v ?? 'uint16' })}
                options={opts(MODBUS_DATA_TYPES, (k) => k)}
              />
              <Input aria-label={`${t('form.scale')} ${i + 1}`} placeholder="×1" value={r.scale} onChange={(e) => update(r.key, { scale: e.currentTarget.value })} />
              <Input aria-label={`${t('form.unit')} ${i + 1}`} placeholder={t('form.unit')} value={r.unit} maxLength={32} onChange={(e) => update(r.key, { unit: e.currentTarget.value })} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────────── connection test ───────────── */

/**
 * "Ulanishni tekshirish": validates the draft, sends it to the test endpoint (stored secrets are merged server-side
 * for an existing connector) and shows the result: ok / error, latency, sensors found.
 */
export function ConnectionTest({
  ctx,
  connectorId,
  getConfig,
  disabled,
}: {
  ctx: ConnectorsContext;
  connectorId: string | null;
  /** the validated draft config, or null when the draft has errors (they are shown by the caller) */
  getConfig: () => ConnectorConfigInput | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation('connectors');
  const test = useTestConnector(ctx);
  const [result, setResult] = useState<ConnectorTestResult | null>(null);
  const run = async () => {
    const config = getConfig();
    if (!config) return;
    setResult(null);
    try {
      setResult(await test.mutateAsync({ id: connectorId, input: { config } }));
    } catch (err) {
      setResult({ ok: false, latencyMs: null, sensorsFound: null, message: err instanceof Error ? err.message : String(err), checkedAt: new Date().toISOString() });
    }
  };
  return (
    <div className="flex flex-col gap-8">
      <Button variant="secondary" leftIcon={RiPulseLine} loading={test.isPending} disabled={disabled} onClick={() => void run()}>
        {test.isPending ? t('test.running') : t('test.run')}
      </Button>
      {result && <TestResultCard result={result} />}
    </div>
  );
}

export function TestResultCard({ result, className }: { result: ConnectorTestResult; className?: string }) {
  const { t } = useTranslation('connectors');
  const facts = [
    result.latencyMs != null ? t('test.latency', { ms: result.latencyMs }) : null,
    result.sensorsFound != null ? t('test.sensors', { count: result.sensorsFound }) : null,
    formatReadingTime(result.checkedAt),
  ].filter(Boolean);
  return (
    <output
      className={cn('flex items-start gap-10 rounded-8 px-12 py-10', result.ok ? 'bg-tone-green-10 text-tone-green' : 'bg-tone-red-10 text-tone-red-lighter', className)}
    >
      {result.ok ? <RiCheckboxCircleLine aria-hidden className="mt-1 size-16 shrink-0" /> : <RiCloseCircleLine aria-hidden className="mt-1 size-16 shrink-0" />}
      <div className="flex min-w-0 flex-col gap-2">
        <span className="text-13 font-semibold">{result.ok ? t('test.ok') : t('test.failed')}</span>
        {result.message && <span className="text-xs break-words text-fg-secondary">{result.message}</span>}
        <span className="text-xs text-fg-secondary tabular-nums">{facts.join(' · ')}</span>
      </div>
    </output>
  );
}
